import type { Draft, Receipt } from "../domain/types";
import type { SubmissionRequest } from "../domain/submission";

const DB_NAME = "pitchdog-deck-discovery";
const DB_VERSION = 1;

export type OutboxItem = {
  id: string;
  request: SubmissionRequest;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  lastError?: string;
};

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export function indexedDbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

export async function openDiscoveryDb(): Promise<IDBDatabase> {
  if (!indexedDbAvailable()) throw new Error("IndexedDB is unavailable");
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains("drafts")) database.createObjectStore("drafts", { keyPath: "draftId" });
    if (!database.objectStoreNames.contains("outbox")) database.createObjectStore("outbox", { keyPath: "id" });
    if (!database.objectStoreNames.contains("receipts")) database.createObjectStore("receipts", { keyPath: "submissionId" });
  };
  return requestToPromise(request);
}

async function put(storeName: "drafts" | "outbox" | "receipts", value: Draft | OutboxItem | Receipt): Promise<void> {
  const database = await openDiscoveryDb();
  const transaction = database.transaction(storeName, "readwrite");
  transaction.objectStore(storeName).put(value);
  await transactionDone(transaction);
  database.close();
}

async function get<T>(storeName: "drafts" | "outbox" | "receipts", key: IDBValidKey): Promise<T | undefined> {
  const database = await openDiscoveryDb();
  const transaction = database.transaction(storeName, "readonly");
  const value = await requestToPromise(transaction.objectStore(storeName).get(key));
  await transactionDone(transaction);
  database.close();
  return value as T | undefined;
}

export async function saveDraft(draft: Draft): Promise<void> {
  await put("drafts", draft);
}

export async function loadDraft(draftId: string): Promise<Draft | undefined> {
  return get<Draft>("drafts", draftId);
}

export async function saveOutbox(item: OutboxItem): Promise<void> {
  await put("outbox", item);
}

export async function getOutbox(id: string): Promise<OutboxItem | undefined> {
  return get<OutboxItem>("outbox", id);
}

export async function listOutbox(): Promise<OutboxItem[]> {
  const database = await openDiscoveryDb();
  const transaction = database.transaction("outbox", "readonly");
  const values = await requestToPromise(transaction.objectStore("outbox").getAll());
  await transactionDone(transaction);
  database.close();
  return values as OutboxItem[];
}

export async function deleteOutbox(id: string): Promise<void> {
  const database = await openDiscoveryDb();
  const transaction = database.transaction("outbox", "readwrite");
  transaction.objectStore("outbox").delete(id);
  await transactionDone(transaction);
  database.close();
}

export async function saveReceipt(receipt: Receipt): Promise<void> {
  await put("receipts", receipt);
}

export async function getReceipt(submissionId: string): Promise<Receipt | undefined> {
  return get<Receipt>("receipts", submissionId);
}

export async function clearDraft(draftId: string): Promise<void> {
  const database = await openDiscoveryDb();
  const transaction = database.transaction("drafts", "readwrite");
  transaction.objectStore("drafts").delete(draftId);
  await transactionDone(transaction);
  database.close();
}
